import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatCode, normalizeCode } from '@shared/protocol';
import type { MeetingPublic, Participant, RtcCredentials } from '@shared/protocol';
import { ControlBar } from '../components/ControlBar';
import { Lobby } from '../components/Lobby';
import { Masthead } from '../components/Masthead';
import { VideoTile } from '../components/VideoTile';
import { api } from '../lib/api';
import { SignalClient, type ConnectionState } from '../lib/signalClient';
import { useBackground } from '../lib/useBackground';
import { useFullscreen } from '../lib/useFullscreen';
import { useLocalMedia } from '../lib/useLocalMedia';
import { usePointerIdle } from '../lib/usePointerIdle';
import { useRoomMedia } from '../lib/useRoomMedia';

type Phase = 'lobby' | 'call' | 'ended';

const NAME_KEY = 'vortex.name';

/** Stable identity, so a peer who has not been subscribed to yet does not
    re-run the tile's attach effect on every render. */
const NO_TRACKS: never[] = [];

export default function JoinPage({ rawCode }: { rawCode: string }) {
  const code = useMemo(() => normalizeCode(rawCode), [rawCode]);

  const [meeting, setMeeting] = useState<MeetingPublic | null>(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [phase, setPhase] = useState<Phase>('lobby');
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '');
  const [self, setSelf] = useState<Participant | null>(null);
  const [peers, setPeers] = useState<Participant[]>([]);
  const [rtc, setRtc] = useState<RtcCredentials | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);

  /* Capture waits for the lookup. The meeting's profile decides the capture
     constraints — starting on the defaults and re-acquiring once it arrives
     would blink the camera and, in a studio meeting, briefly open the
     microphone with echo cancellation on. */
  const ready = (phase === 'lobby' || phase === 'call') && meeting !== null;
  const media = useLocalMedia(ready, meeting?.media);
  const background = useBackground(media.stream, ready);
  // Publish the processed stream, never the raw one — see useBackground.
  const room = useRoomMedia(phase === 'call' ? rtc : null, background.stream, meeting?.media);
  const clientRef = useRef<SignalClient | null>(null);
  /* The whole call shell goes fullscreen, not the stage alone, so the meeting
     name and the controls come with it. */
  const shellRef = useRef<HTMLDivElement | null>(null);
  const fullscreen = useFullscreen(shellRef);
  /* Fullscreen only: in a window the control bar is part of the page and has
     nothing to hide from. */
  const idle = usePointerIdle(fullscreen.active);

  /* Look the meeting up first so the page can name it before asking for the
     camera — being asked for a camera by an unidentified page is unnerving. */
  useEffect(() => {
    if (!code) {
      setLookupFailed(true);
      return;
    }
    api
      .lookup(code)
      .then(setMeeting)
      .catch(() => setLookupFailed(true));
  }, [code]);

  const leave = useCallback(() => {
    clientRef.current?.leave();
    clientRef.current = null;
    // Clearing rtc tears the SFU connection down through useRoomMedia's cleanup.
    setRtc(null);
    media.stop();
    setPhase('ended');
  }, [media]);

  const join = useCallback(() => {
    if (!code || clientRef.current) return;

    const displayName = name.trim() || 'Guest';
    localStorage.setItem(NAME_KEY, displayName);

    const client = new SignalClient({
      onState: setConnection,
      onJoined: (me, meetingInfo, existing, credentials) => {
        setSelf(me);
        setMeeting(meetingInfo);
        setPeers(existing);
        setJoinError(null);
        // Only set once: a signalling reconnect re-issues a token, and swapping
        // it would tear down a perfectly healthy media connection.
        setRtc((prev) => prev ?? credentials);
      },
      onPeerJoined: (participant) =>
        setPeers((prev) => (prev.some((p) => p.id === participant.id) ? prev : [...prev, participant])),
      onPeerLeft: (id) => setPeers((prev) => prev.filter((p) => p.id !== id)),
      onPeerState: (id, state) =>
        setPeers((prev) => prev.map((p) => (p.id === id ? { ...p, ...state } : p))),
      onChat: () => {},
      onError: (_code, message) => {
        setJoinError(message);
        setPhase('lobby');
      },
    });

    clientRef.current = client;
    client.join({
      code,
      name: displayName,
      audioEnabled: media.audioEnabled,
      videoEnabled: media.videoEnabled,
    });
    setPhase('call');
  }, [code, name, media.audioEnabled, media.videoEnabled]);

  // Keep peers' placeholders honest when the local toggles change.
  useEffect(() => {
    if (phase !== 'call') return;
    clientRef.current?.setLocalState({
      audioEnabled: media.audioEnabled,
      videoEnabled: media.videoEnabled,
    });
  }, [phase, media.audioEnabled, media.videoEnabled]);

  /* Space toggles the microphone, the one shortcut people arrive expecting —
     and the only control still reachable once the pointer has gone idle and
     taken the bar with it.

     Ignored while a control has focus, because space is also "press the
     focused button": muting from the keyboard with the mute button focused
     would otherwise toggle twice and land back where it started. Text fields
     are left alone for the obvious reason, and a held key repeats, so only the
     first event counts. */
  useEffect(() => {
    if (phase !== 'call') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, button, a, [contenteditable]')) return;
      // Otherwise the page scrolls under the call.
      e.preventDefault();
      media.setAudioEnabled(!media.audioEnabled);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, media.audioEnabled, media.setAudioEnabled]);

  useEffect(() => () => clientRef.current?.leave(), []);

  if (lookupFailed) {
    return (
      <div className="shell">
        <Masthead />
        <main className="page">
          <h1>This link is not valid</h1>
          <p className="lede">
            The meeting may have ended or expired, or the link may have been cut short when it was
            copied. Ask the host for a fresh link.
          </p>
        </main>
      </div>
    );
  }

  if (phase === 'lobby' && !meeting) {
    return (
      <div className="shell">
        <Masthead>{code ? <span className="faint">{formatCode(code)}</span> : null}</Masthead>
        <main className="page">
          <p className="faint">Loading…</p>
        </main>
      </div>
    );
  }

  if (phase === 'ended') {
    return (
      <div className="shell">
        <Masthead />
        <main className="page">
          <h1>You left the meeting</h1>
          <p className="lede">{meeting?.title}</p>
          <p style={{ marginTop: '1.5rem' }}>
            <button className="primary" onClick={() => location.reload()}>
              Rejoin
            </button>
          </p>
        </main>
      </div>
    );
  }

  if (phase === 'lobby') {
    return (
      <div className="shell">
        <Masthead>{code ? <span className="faint">{formatCode(code)}</span> : null}</Masthead>
        {joinError ? (
          <div className="page" style={{ paddingBottom: 0 }}>
            <p className="notice bad" role="alert">
              {joinError}
            </p>
          </div>
        ) : null}
        <Lobby
          meeting={meeting}
          media={media}
          background={background}
          name={name}
          onNameChange={setName}
          onJoin={join}
          joining={connection === 'connecting'}
        />
      </div>
    );
  }

  const tiles = peers.length + 1;
  const columns = Math.min(Math.ceil(Math.sqrt(tiles)), 4);

  return (
    <div className={`shell${idle ? ' idle' : ''}`} ref={shellRef}>
      <Masthead>
        <span>{meeting?.title}</span>
        <span className="faint">{tiles === 1 ? 'Waiting for others' : `${tiles} in the call`}</span>
      </Masthead>

      <main className="page-wide" style={{ flex: 1, minHeight: 0 }}>
        {room.error ? (
          <p className="notice bad" role="alert" style={{ margin: '0.5rem' }}>
            {room.error}
          </p>
        ) : null}

        <div className="stage" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          <VideoTile
            name={self?.name ?? (name || 'You')}
            stream={background.stream}
            videoEnabled={media.videoEnabled}
            audioEnabled={media.audioEnabled}
            isSelf
            selfAudioEnabled={media.audioEnabled}
            onToggleSelfAudio={() => media.setAudioEnabled(!media.audioEnabled)}
          />
          {peers.map((p) => (
            <VideoTile
              key={p.id}
              name={p.name}
              tracks={room.tracks.get(p.id) ?? NO_TRACKS}
              videoEnabled={p.videoEnabled}
              audioEnabled={p.audioEnabled}
              speaking={room.speaking.has(p.id)}
              sinkId={media.choice.audioOutput}
              selfAudioEnabled={media.audioEnabled}
              onToggleSelfAudio={() => media.setAudioEnabled(!media.audioEnabled)}
            />
          ))}
        </div>

        <ControlBar
          audioEnabled={media.audioEnabled}
          videoEnabled={media.videoEnabled}
          onToggleAudio={() => media.setAudioEnabled(!media.audioEnabled)}
          onToggleVideo={() => media.setVideoEnabled(!media.videoEnabled)}
          onLeave={leave}
          fullscreen={fullscreen.active}
          onToggleFullscreen={fullscreen.supported ? fullscreen.toggle : undefined}
          blurOn={background.choice.mode !== 'none'}
          blurLoading={background.loading}
          onToggleBlur={
            background.supported
              ? () =>
                  background.setChoice(
                    background.choice.mode === 'none' ? { mode: 'blur' } : { mode: 'none' },
                  )
              : undefined
          }
          status={
            connection === 'reconnecting'
              ? 'Reconnecting…'
              : rtc && !room.connected
                ? 'Connecting media…'
                : undefined
          }
        />
      </main>
    </div>
  );
}
